/**
 * Tipos para el Modulo de Gestion de Requisitos INDRA
 * PECAL 2210 / ISO 29148 / CMMI REQM
 */

// Enums de clasificacion
export type RequirementCategory =
  | 'functional'
  | 'non_functional'
  | 'technical'
  | 'legal'
  | 'environmental'
  | 'security';

export type RequirementLevel = 'str' | 'sys' | 'srs' | 'des' | 'tst';

export type RequirementPriority = 'critical' | 'high' | 'medium' | 'low';

export type RequirementStatus =
  | 'draft'
  | 'proposed'
  | 'approved'
  | 'implemented'
  | 'verified'
  | 'obsolete'
  | 'rejected';

export type SourceType =
  | 'stakeholder'
  | 'contract'
  | 'regulation'
  | 'standard'
  | 'derived'
  | 'internal';

export type VerificationMethod = 'inspection' | 'analysis' | 'demonstration' | 'test';

export type VerificationStatus =
  | 'pending'
  | 'in_progress'
  | 'passed'
  | 'failed'
  | 'waived'
  | 'not_applicable';

export type LinkType =
  | 'satisfies'
  | 'derives_from'
  | 'verifies'
  | 'mitigates'
  | 'implements'
  | 'conflicts_with'
  | 'related_to';

export type LinkTargetType =
  | 'requirement'
  | 'test_case'
  | 'design_element'
  | 'risk'
  | 'change_request'
  | 'work_order'
  | 'document'
  | 'quality_control';

export type LinkStatus = 'active' | 'pending_review' | 'broken' | 'obsolete';

export type ChangeType =
  | 'created'
  | 'modified'
  | 'status_changed'
  | 'approved'
  | 'rejected'
  | 'obsoleted'
  | 'version_updated'
  | 'link_added'
  | 'link_removed';

export type ENSImplementationStatus =
  | 'not_started'
  | 'in_progress'
  | 'implemented'
  | 'verified'
  | 'not_applicable';

export type FrameworkType =
  | 'security'
  | 'quality'
  | 'environmental'
  | 'legal'
  | 'defense'
  | 'aerospace';

// Interfaces principales
export interface Requirement {
  id: string;
  requirement_id: string;
  version: string;
  category: RequirementCategory;
  subcategory?: string;
  level: RequirementLevel;
  title: string;
  description: string;
  rationale?: string;
  acceptance_criteria?: string;
  source_type: SourceType;
  source_reference?: string;
  parent_requirement_id?: string;
  priority: RequirementPriority;
  status: RequirementStatus;
  risk_level?: string;
  verification_method?: VerificationMethod;
  verification_status: VerificationStatus;
  verified_by?: string;
  verified_at?: string;
  assigned_to?: string;
  responsible_team?: string;
  compliance_standards: string[];
  ens_measures: string[];
  wbs_reference?: string;
  estimated_effort_hours?: number;
  target_release?: string;
  created_by?: string;
  created_at: string;
  updated_by?: string;
  updated_at: string;
  approved_by?: string;
  approved_at?: string;
  // Campos de vista
  created_by_name?: string;
  assigned_to_name?: string;
  verified_by_name?: string;
  approved_by_name?: string;
  parent_requirement_code?: string;
  parent_requirement_title?: string;
  outgoing_links_count?: number;
  incoming_links_count?: number;
  children_count?: number;
}

export interface RequirementLink {
  id: string;
  source_requirement_id: string;
  target_type: LinkTargetType;
  target_id: string;
  target_reference?: string;
  link_type: LinkType;
  link_status: LinkStatus;
  coverage_percentage?: number;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface RequirementChange {
  id: string;
  requirement_id: string;
  from_version?: string;
  to_version: string;
  change_type: ChangeType;
  field_changed?: string;
  old_value?: string;
  new_value?: string;
  change_reason?: string;
  impact_analysis?: string;
  affected_requirements?: string[];
  change_request_id?: string;
  changed_by?: string;
  changed_at: string;
  // Campos de vista
  changed_by_name?: string;
}

export interface ComplianceFramework {
  id: string;
  code: string;
  name: string;
  version?: string;
  description?: string;
  framework_type: FrameworkType;
  structure?: Record<string, unknown>;
  is_mandatory: boolean;
  applicable_to: string[];
  effective_date?: string;
  expiry_date?: string;
  source_url?: string;
  created_at: string;
  updated_at: string;
}

export interface ENSMeasure {
  id: string;
  measure_code: string;
  category: string;
  subcategory: string;
  name: string;
  description?: string;
  level_low: boolean;
  level_medium: boolean;
  level_high: boolean;
  implementation_guidance?: string;
  verification_criteria?: string;
  is_implemented: boolean;
  implementation_status: ENSImplementationStatus;
  implementation_notes?: string;
  evidence_documents?: string[];
  responsible_role?: string;
  created_at: string;
  updated_at: string;
}

// Estadisticas y metricas
export interface RequirementsStats {
  total_requirements: number;
  by_status: Record<RequirementStatus, number>;
  by_category: Record<RequirementCategory, number>;
  by_priority: Record<RequirementPriority, number>;
  by_verification: Record<VerificationStatus, number>;
  coverage_percentage: number;
}

export interface TraceabilityCoverage {
  total_requirements: number;
  with_parent_link: number;
  with_children: number;
  with_test_coverage: number;
  with_design_link: number;
  fully_traced: number;
  coverage_percentage: number;
}

// Filtros
export interface RequirementFilters {
  search?: string;
  category?: RequirementCategory | 'all';
  level?: RequirementLevel | 'all';
  status?: RequirementStatus | 'all';
  priority?: RequirementPriority | 'all';
  verification_status?: VerificationStatus | 'all';
  assigned_to?: string;
  compliance_standard?: string;
  ens_measure?: string;
  parent_id?: string;
  created_from?: string;
  created_to?: string;
}

// Formularios
export interface RequirementFormData {
  category: RequirementCategory;
  subcategory?: string;
  level: RequirementLevel;
  title: string;
  description: string;
  rationale?: string;
  acceptance_criteria?: string;
  source_type: SourceType;
  source_reference?: string;
  parent_requirement_id?: string;
  priority: RequirementPriority;
  verification_method?: VerificationMethod;
  assigned_to?: string;
  responsible_team?: string;
  compliance_standards: string[];
  ens_measures: string[];
  wbs_reference?: string;
  estimated_effort_hours?: number;
  target_release?: string;
}

export interface RequirementLinkFormData {
  source_requirement_id: string;
  target_type: LinkTargetType;
  target_id: string;
  target_reference?: string;
  link_type: LinkType;
  notes?: string;
}

// Labels para UI
export const categoryLabels: Record<RequirementCategory, string> = {
  functional: 'Funcional',
  non_functional: 'No Funcional',
  technical: 'Tecnico',
  legal: 'Legal',
  environmental: 'Ambiental',
  security: 'Seguridad',
};

export const levelLabels: Record<RequirementLevel, string> = {
  str: 'Stakeholder (STR)',
  sys: 'Sistema (SYS)',
  srs: 'Software (SRS)',
  des: 'Diseno (DES)',
  tst: 'Prueba (TST)',
};

export const priorityLabels: Record<RequirementPriority, string> = {
  critical: 'Critico',
  high: 'Alto',
  medium: 'Medio',
  low: 'Bajo',
};

export const statusLabels: Record<RequirementStatus, string> = {
  draft: 'Borrador',
  proposed: 'Propuesto',
  approved: 'Aprobado',
  implemented: 'Implementado',
  verified: 'Verificado',
  obsolete: 'Obsoleto',
  rejected: 'Rechazado',
};

export const sourceTypeLabels: Record<SourceType, string> = {
  stakeholder: 'Stakeholder',
  contract: 'Contrato',
  regulation: 'Regulacion',
  standard: 'Estandar',
  derived: 'Derivado',
  internal: 'Interno',
};

export const verificationMethodLabels: Record<VerificationMethod, string> = {
  inspection: 'Inspeccion',
  analysis: 'Analisis',
  demonstration: 'Demostracion',
  test: 'Prueba',
};

export const verificationStatusLabels: Record<VerificationStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  passed: 'Aprobado',
  failed: 'Fallido',
  waived: 'Dispensado',
  not_applicable: 'No Aplica',
};

export const linkTypeLabels: Record<LinkType, string> = {
  satisfies: 'Satisface',
  derives_from: 'Deriva de',
  verifies: 'Verifica',
  mitigates: 'Mitiga',
  implements: 'Implementa',
  conflicts_with: 'Conflicto con',
  related_to: 'Relacionado con',
};

export const linkTargetTypeLabels: Record<LinkTargetType, string> = {
  requirement: 'Requisito',
  test_case: 'Caso de Prueba',
  design_element: 'Elemento de Diseno',
  risk: 'Riesgo',
  change_request: 'Solicitud de Cambio',
  work_order: 'Orden de Trabajo',
  document: 'Documento',
  quality_control: 'Control de Calidad',
};

export const ensImplementationStatusLabels: Record<ENSImplementationStatus, string> = {
  not_started: 'No Iniciado',
  in_progress: 'En Progreso',
  implemented: 'Implementado',
  verified: 'Verificado',
  not_applicable: 'No Aplica',
};

// Colores para UI
export const priorityColors: Record<RequirementPriority, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200',
};

export const statusColors: Record<RequirementStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  proposed: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  implemented: 'bg-purple-100 text-purple-800',
  verified: 'bg-emerald-100 text-emerald-800',
  obsolete: 'bg-slate-100 text-slate-800',
  rejected: 'bg-red-100 text-red-800',
};

export const categoryColors: Record<RequirementCategory, string> = {
  functional: 'bg-blue-100 text-blue-800',
  non_functional: 'bg-purple-100 text-purple-800',
  technical: 'bg-cyan-100 text-cyan-800',
  legal: 'bg-amber-100 text-amber-800',
  environmental: 'bg-green-100 text-green-800',
  security: 'bg-red-100 text-red-800',
};

export const levelColors: Record<RequirementLevel, string> = {
  str: 'bg-indigo-100 text-indigo-800',
  sys: 'bg-violet-100 text-violet-800',
  srs: 'bg-fuchsia-100 text-fuchsia-800',
  des: 'bg-pink-100 text-pink-800',
  tst: 'bg-rose-100 text-rose-800',
};

export const verificationStatusColors: Record<VerificationStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  waived: 'bg-yellow-100 text-yellow-800',
  not_applicable: 'bg-slate-100 text-slate-600',
};

// Iconos por categoria (para usar con lucide-react)
export const categoryIcons: Record<RequirementCategory, string> = {
  functional: 'Boxes',
  non_functional: 'Gauge',
  technical: 'Cpu',
  legal: 'Scale',
  environmental: 'Leaf',
  security: 'Shield',
};

// ==================== FASE 2: LINEAS BASE Y TRAZABILIDAD ====================

export type BaselineType = 'functional' | 'allocated' | 'product' | 'development' | 'release';

export type BaselineStatus = 'draft' | 'under_review' | 'approved' | 'frozen' | 'superseded' | 'cancelled';

export interface RequirementBaseline {
  id: string;
  baseline_id: string;
  name: string;
  description?: string;
  baseline_type: BaselineType;
  status: BaselineStatus;
  requirements_snapshot: RequirementSnapshot[];
  total_requirements: number;
  metrics: BaselineMetrics;
  approved_by?: string;
  approved_at?: string;
  approval_notes?: string;
  effective_from?: string;
  effective_until?: string;
  superseded_by?: string;
  superseded_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Campos de vista
  created_by_name?: string;
  approved_by_name?: string;
  coverage_percentage?: number;
  verified_count?: number;
  superseded_by_code?: string;
}

export interface RequirementSnapshot {
  id: string;
  requirement_id: string;
  version: string;
  title: string;
  category: RequirementCategory;
  level: RequirementLevel;
  status: RequirementStatus;
  priority: RequirementPriority;
  verification_status: VerificationStatus;
}

export interface BaselineMetrics {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_category: Record<string, number>;
  verified_count: number;
  coverage_percentage: number;
}

export interface BaselineRequirement {
  id: string;
  baseline_id: string;
  requirement_id: string;
  requirement_version: string;
  requirement_code: string;
  status_at_snapshot: RequirementStatus;
  priority_at_snapshot: RequirementPriority;
  included_at: string;
  included_by?: string;
  inclusion_notes?: string;
}

export interface TraceabilityValidation {
  requirement_id: string;
  requirement_code: string;
  has_parent: boolean;
  has_children: boolean;
  has_test_link: boolean;
  has_design_link: boolean;
  has_work_order_link: boolean;
  traceability_score: number;
  issues: string[];
  recommendations: string[];
}

export interface TraceabilityGraphNode {
  id: string;
  label: string;
  title: string;
  category: RequirementCategory;
  level: RequirementLevel;
  status: RequirementStatus;
  priority: RequirementPriority;
  verification_status: VerificationStatus;
  depth?: number;
}

export interface TraceabilityGraphEdge {
  source: string;
  target: string;
  type: string;
  label: string;
  target_type?: LinkTargetType;
}

export interface TraceabilityGraph {
  nodes: TraceabilityGraphNode[];
  edges: TraceabilityGraphEdge[];
}

export interface TraceabilityCoverageExtended {
  total_requirements: number;
  with_parent_link: number;
  with_children: number;
  with_test_coverage: number;
  with_design_link: number;
  fully_traced: number;
  orphan_requirements: number;
  coverage_percentage: number;
  by_level: Record<string, { total: number; traced: number; percentage: number }>;
  by_category: Record<string, { total: number; with_tests: number }>;
}

export interface BaselineComparison {
  comparison_type: 'added' | 'removed' | 'modified';
  requirement_id: string;
  requirement_code: string;
  title_a?: string;
  title_b?: string;
  status_a?: RequirementStatus;
  status_b?: RequirementStatus;
  change_description: string;
}

export interface BaselineFormData {
  name: string;
  description?: string;
  baseline_type: BaselineType;
  requirement_ids: string[];
}

// Labels para Fase 2
export const baselineTypeLabels: Record<BaselineType, string> = {
  functional: 'Funcional',
  allocated: 'Asignada',
  product: 'Producto',
  development: 'Desarrollo',
  release: 'Release',
};

export const baselineStatusLabels: Record<BaselineStatus, string> = {
  draft: 'Borrador',
  under_review: 'En Revision',
  approved: 'Aprobada',
  frozen: 'Congelada',
  superseded: 'Reemplazada',
  cancelled: 'Cancelada',
};

export const baselineStatusColors: Record<BaselineStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  frozen: 'bg-blue-100 text-blue-800',
  superseded: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-800',
};

export const baselineTypeColors: Record<BaselineType, string> = {
  functional: 'bg-indigo-100 text-indigo-800',
  allocated: 'bg-violet-100 text-violet-800',
  product: 'bg-emerald-100 text-emerald-800',
  development: 'bg-cyan-100 text-cyan-800',
  release: 'bg-amber-100 text-amber-800',
};
